import React, {
    ReactNode,
    forwardRef,
    useEffect,
    useRef,
    useState,
    useCallback,
} from 'react';

import { Box, SxProps, Theme } from '@mui/material';

const DEFAULT_MAX_HEIGHT = 400;
const BOTTOM_SPACING = 16;
const MIN_HEIGHT = 60;

export const useAvailableHeight = (
    elementRef: React.RefObject<HTMLDivElement>,
    defaultMaxHeight: number = DEFAULT_MAX_HEIGHT
) => {
    const [availableHeight, setAvailableHeight] =
        useState<number>(defaultMaxHeight);

    const updateHeight = useCallback(() => {
        if (!elementRef.current) {
            return;
        }

        const rect = elementRef.current.getBoundingClientRect();
        if (rect.top === 0 && rect.bottom === 0) {
            return;
        }

        let bottomBoundary = window.innerHeight;

        let parent = elementRef.current.parentElement;
        while (
            parent &&
            parent !== document.body &&
            parent !== document.documentElement
        ) {
            const style = window.getComputedStyle(parent);
            const isOverflowContainer =
                style.overflowY === 'hidden' ||
                style.overflowY === 'auto' ||
                style.overflowY === 'scroll' ||
                style.overflow === 'hidden' ||
                style.overflow === 'auto' ||
                style.overflow === 'scroll' ||
                parent.hasAttribute('data-panel');

            if (isOverflowContainer) {
                const parentRect = parent.getBoundingClientRect();
                if (
                    parentRect.height > 0 &&
                    parentRect.bottom < bottomBoundary
                ) {
                    bottomBoundary = parentRect.bottom;
                }
            }
            parent = parent.parentElement;
        }

        const calculatedAvailable = bottomBoundary - rect.top - BOTTOM_SPACING;
        const targetMaxHeight = Math.max(
            MIN_HEIGHT,
            Math.min(defaultMaxHeight, calculatedAvailable)
        );

        setAvailableHeight((prev) => {
            return Math.abs(prev - targetMaxHeight) >= 1
                ? targetMaxHeight
                : prev;
        });
    }, [defaultMaxHeight, elementRef]);

    useEffect(() => {
        updateHeight();

        const handleResizeOrScroll = () => {
            updateHeight();
        };

        window.addEventListener('resize', handleResizeOrScroll);
        window.addEventListener('scroll', handleResizeOrScroll, true);
        document.addEventListener('mousemove', handleResizeOrScroll);

        let resizeObserver: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined' && elementRef.current) {
            resizeObserver = new ResizeObserver(() => {
                updateHeight();
            });

            if (elementRef.current.parentElement) {
                resizeObserver.observe(elementRef.current.parentElement);
            }
            let current = elementRef.current.parentElement;
            while (current && current !== document.body) {
                if (
                    current.hasAttribute('data-panel') ||
                    current.classList.contains('MuiPaper-root') ||
                    current.classList.contains('MuiBox-root')
                ) {
                    resizeObserver.observe(current);
                }
                current = current.parentElement;
            }
        }

        let animationFrameId: number;
        let isRunning = true;
        const loop = () => {
            if (!isRunning) return;
            updateHeight();
            animationFrameId = requestAnimationFrame(loop);
        };
        animationFrameId = requestAnimationFrame(loop);

        return () => {
            isRunning = false;
            cancelAnimationFrame(animationFrameId);
            window.removeEventListener('resize', handleResizeOrScroll);
            window.removeEventListener('scroll', handleResizeOrScroll, true);
            document.removeEventListener('mousemove', handleResizeOrScroll);
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
        };
    }, [updateHeight, elementRef]);

    return availableHeight;
};

interface IOwnProps {
    children: ReactNode;
    sx?: SxProps<Theme>;
    maxHeight?: number;
}
const ListContainer = forwardRef<HTMLDivElement, IOwnProps>(
    ({ children, sx, maxHeight = DEFAULT_MAX_HEIGHT }: IOwnProps, ref) => {
        const containerRef = useRef<HTMLDivElement>(null);
        const dynamicMaxHeight = useAvailableHeight(
            containerRef,
            typeof maxHeight === 'number' ? maxHeight : DEFAULT_MAX_HEIGHT
        );

        return (
            <Box
                ref={(node: HTMLDivElement | null) => {
                    (containerRef as any).current = node;
                    if (typeof ref === 'function') {
                        ref(node);
                    } else if (ref) {
                        (ref as any).current = node;
                    }
                }}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: dynamicMaxHeight,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    ...((sx as object) || {}),
                }}
            >
                {children}
            </Box>
        );
    }
);

export default ListContainer;
